import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { createPageUrl } from './utils';
import { Home, Package, Truck, Settings, Menu, FileText, LogOut, User, ChevronUp } from 'lucide-react';
import PWAInstaller from '@/components/PWAInstaller';
import VersionBadge from '@/components/VersionBadge';
import { useAuth } from '@/lib/AuthContext';
import './layout.css';

const MENU_ITEMS = [
  { name: 'Dashboard', path: createPageUrl('Home'), icon: Home },
  { name: 'Mailings', path: createPageUrl('ProcessedMailings'), icon: Package },
  { name: 'Shipments', path: createPageUrl('Shipments'), icon: Truck },
  { name: 'Settings', path: createPageUrl('Settings'), icon: Settings, adminOnly: true },
];

export default function Layout({ children }) {
  const { user: currentUser, signOut, isAdmin } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  /*
   * Hover and click are separate reasons for the menu to be open.
   *
   * Toggling one flag on click made clicking the row close a menu that hover
   * had just opened - a click is preceded by the pointer arriving. Hover opens
   * it transiently; a click pins it, which is what a touch screen relies on
   * since it has no hover at all.
   */
  const [hoveringAccount, setHoveringAccount] = useState(false);
  const [accountPinned, setAccountPinned] = useState(false);
  const accountOpen = hoveringAccount || accountPinned;

  const closeAccount = () => {
    setHoveringAccount(false);
    setAccountPinned(false);
  };

  // Escape closes it, as it does any other menu.
  useEffect(() => {
    if (!accountOpen) return undefined;
    const onKey = (e) => e.key === 'Escape' && closeAccount();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [accountOpen]);

  // AuthContext exposes signOut; this destructured `logout`, which is
  // undefined, so `await logout()` threw a TypeError straight into the catch
  // below and the button did nothing at all - silently, on a shared shop
  // machine where signing out is the whole point.
  const handleLogout = async () => {
    try {
      await signOut();
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <div className={`sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-logo">
          <h1>
            <FileText size={32} />
            EDDM Organizer
          </h1>
          <p>USPS Mail Management</p>
          <VersionBadge />
        </div>
        <ul className="sidebar-menu">
          {MENU_ITEMS.filter((item) => !item.adminOnly || isAdmin).map((item) => (
            <li key={item.name} className="sidebar-menu-item">
              <NavLink
                to={item.path}
                className={({ isActive }) => `sidebar-menu-link ${isActive ? 'active' : ''}`}
                onClick={() => setIsSidebarOpen(false)}
              >
                <item.icon size={20} />
                {item.name}
              </NavLink>
            </li>
          ))}
        </ul>
        
        {/*
          * Signing out lives on the account rather than beside it.
          *
          * Hover opens it, but not only hover: a touch screen has none, and
          * this is installable as an app, so it opens on click and on keyboard
          * focus too. Escape and a click elsewhere close it.
          */}
        <div className="sidebar-logout">
          {currentUser && (
            <div
              className="sidebar-account"
              onMouseEnter={() => setHoveringAccount(true)}
              onMouseLeave={() => setHoveringAccount(false)}
            >
              <button
                type="button"
                className="sidebar-user"
                aria-haspopup="menu"
                aria-expanded={accountOpen}
                onClick={() => setAccountPinned((pinned) => !pinned)}
                onFocus={() => setAccountPinned(true)}
              >
                <div className="sidebar-user-icon">
                  <User size={20} />
                </div>
                <div className="sidebar-user-info">
                  <div className="sidebar-user-name">
                    {currentUser.full_name || currentUser.email}
                  </div>
                  <div className="sidebar-user-email">{currentUser.email}</div>
                </div>
                <ChevronUp size={16} className={`sidebar-account-caret ${accountOpen ? 'open' : ''}`} />
              </button>

              {accountOpen && (
                <div className="sidebar-account-menu" role="menu">
                  <button
                    type="button"
                    role="menuitem"
                    className="sidebar-account-item"
                    onClick={handleLogout}
                  >
                    <LogOut size={16} />
                    Log Out
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Sidebar Overlay for Mobile */}
      <div
        className={`sidebar-overlay ${isSidebarOpen ? 'visible' : ''}`}
        onClick={() => setIsSidebarOpen(false)}
      />

      {/* Main Content */}
      <div className="main-content">
        <button 
          className="hamburger-menu"
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          aria-label="Toggle sidebar"
        >
          <Menu size={24} />
        </button>
        {children}
      </div>

      {/* PWA Installer */}
      <PWAInstaller />
    </div>
  );
}